import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { DetailRequest, FilterBuilder, FilterPage, FilterQueryMode, FilterSort, Status, translateCommonErrorStatus } from "@core/data";
import { AdvancedReportOptionService } from "@core/data/api/reports/advanced-report-option.service";
import { AdvancedReportService } from "@core/data/api/reports/advanced-report.service";
import { ADVANCED_REPORT_OPTION_DEFAULT, AdvancedReport, NewReportOption, ReportAccess } from "@core/data/types/advanced-report";
import { SampleType } from "@library";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { forkJoin } from "rxjs";
import { tap } from "rxjs/operators";
import { AddReportPage } from "../report-view/report-state/advanced-report.state-actions";

export interface AdvancedReportIndexStateModel {
    orgKey: string;
    status: Status;

    data: AdvancedReport[];
    total: number;

    // filter options
    page: FilterPage;
    sort: FilterSort;
    // query:

    // table options
    displayColumns: string[];
    sortOptions: { id: string, label: string }[];
    pageSizeOptions: number[];
}

// TODO review and add query to state
export interface AdvancedReportIndexQuery {
    scionCropId: string[];
    birthYear?: number[];
    birthWeek?: number[];
}

const data = {
    sample: {
        label: "Apple 1"
    }
}

export const ADVANCED_REPORT_SORT_OPTIONS: { id: string, label: string }[] = [
    { id: 'updatedAt', label: 'Updated Date' },
    { id: 'createdAt', label: 'Created Date' },
    { id: 'label', label: 'Title' },
]

export const ADVANCED_REPORT_COLUMNS = [
    'label',
    'actions',
    'timestamps',
];

const DEFAULTS: AdvancedReportIndexStateModel = {
    orgKey: null,
    status: Status.UNINITIALIZED,
    data: [],
    total: 0,
    page: {
        index: 0,
        size: 20
    },
    sort: {
        column: 'updatedAt',
        order: 'desc'
    },
    displayColumns: ADVANCED_REPORT_COLUMNS,
    sortOptions: ADVANCED_REPORT_SORT_OPTIONS,
    pageSizeOptions: [20,50,100,500,1000],
}

export class InitAdvancedReportIndex {
    static readonly type = "[AdvancedReportIndex] Init";
    constructor(public orgKey: string){}
}

export class LoadAdvancedReportIndex {
    static readonly type = "[AdvancedReportIndex] Load";
    constructor(){}
}

export class NewAdvancedReport {
    static readonly type = "[AdvancedReportIndex] New Report";
    constructor(){}
}

export class AddAdvancedReportOptions {
    static readonly type = "[AdvancedReportIndex] Add Report Options";
    constructor(
        public reportKey: string,
    ) {}
}

export class SortAdvancedReportIndex {
    static readonly type = "[AdvancedReportIndex] Sort";
    constructor(public column: string, public order = 'asc'){}
}

export class PageAdvancedReportIndex {
    static readonly type = "[AdvancedReportIndex] Page";
    constructor(public index: number, public size: number){}
}

export class DeleteAdvancedReport {
    static readonly type = "[AdvancedReportIndex] Delete Report";
    constructor(public key: string){}
}

@State<AdvancedReportIndexStateModel>({
    name: 'advanced_report_index',
    defaults: DEFAULTS,
})
@Injectable()
export class AdvancedReportIndexState {

    @Selector()
    static sort(state: AdvancedReportIndexStateModel) {
        return state.sort;
    }

    constructor(
        private _zone: NgZone,
        private _router: Router,
        private _reportService: AdvancedReportService,
        private _reportOptionService: AdvancedReportOptionService,
    ) {}

    @Action(InitAdvancedReportIndex)
    initAdvancedReportIndex(ctx: StateContext<AdvancedReportIndexStateModel>, action: InitAdvancedReportIndex) {

        ctx.patchState({
            ...DEFAULTS,
            orgKey: action.orgKey,
        });

        ctx.dispatch(LoadAdvancedReportIndex);
    }

    @Action(LoadAdvancedReportIndex)
    loadAdvancedReportIndex(ctx: StateContext<AdvancedReportIndexStateModel>, action: LoadAdvancedReportIndex) {
        const state = ctx.getState();

        const filter = this.getCurrentFilter(state);

        const detail: DetailRequest = {
            related: [],
        };

        ctx.patchState({
            status: Status.LOADING
        });

        return this._reportService.query(filter, detail)
            .pipe(
                tap(
                    result => {
                        if (result.total > 0) {
                            ctx.patchState({
                                    status: Status.OK,
                                    data: result.data,
                                    total: result.total,
                            });
                        } else {
                            ctx.patchState({
                                status: Status.EMPTY,
                                data: [],
                                total: 0
                            });
                        }
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                            data: [],
                            total: 0
                        });
                    }
                )
            )
    }

    @Action(NewAdvancedReport)
    newAdvancedReport(ctx: StateContext<AdvancedReportIndexStateModel>, action: NewAdvancedReport) {
        const state = ctx.getState();

        // TODO ADD DEFAULT FILTERS
        const report: Partial<AdvancedReport> = {
            label: 'Untitled Document',
            ownerOrgKey: state.orgKey,
            filter: null,
            access: ReportAccess.RESTRICTED,
        };

        ctx.patchState({
            status: Status.LOADING,
        });

        return this._reportService.create(report)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.LOADING,
                        });

                        ctx.dispatch(new AddAdvancedReportOptions(result.key));
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                            data: [],
                            total: 0
                        });
                    }
                )
            );
    }

    @Action(AddAdvancedReportOptions)
    addAdvancedReportOptions(ctx: StateContext<AdvancedReportIndexStateModel>, action: AddAdvancedReportOptions) {
        let state = ctx.getState();

        let options: NewReportOption = {
            reportKey: action.reportKey,
            config: ADVANCED_REPORT_OPTION_DEFAULT,
        }

        return this._reportOptionService.create(options)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.OK,
                        });

                        this._zone.run(() => {
                            this._router.navigate([
                                '/org',
                                state.orgKey,
                                'advanced-reports',
                                action.reportKey,
                            ]);
                        });
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                            data: [],
                            total: 0
                        });
                    }
                )
            )
    }

    @Action(DeleteAdvancedReport)
    deleteAdvancedReport(ctx: StateContext<AdvancedReportIndexStateModel>, action: DeleteAdvancedReport) {
        ctx.patchState({
            status: Status.LOADING,
        });

        return this._reportService.delete(action.key)
            .pipe(
                tap(
                    result => {
                        ctx.dispatch(LoadAdvancedReportIndex);
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                        });
                    }
                )
            )


    }

    @Action(SortAdvancedReportIndex)
    sortAdvancedReportIndex(ctx: StateContext<AdvancedReportIndexStateModel>, action: SortAdvancedReportIndex) {
        const state = ctx.getState();

        ctx.patchState({
            sort: {
                column: action.column,
                order: action.order,
            },
            page: {
                index: 0,
                size: state.page.size,
            }
        });

        return ctx.dispatch(new LoadAdvancedReportIndex);
    }

    @Action(PageAdvancedReportIndex)
    pageAdvancedReportIndex(ctx: StateContext<AdvancedReportIndexStateModel>, action: PageAdvancedReportIndex) {
        ctx.patchState({
            page: {
                index: action.index,
                size: action.size
            }
        });

        return ctx.dispatch(new LoadAdvancedReportIndex);
    }

    private getCurrentFilter(state: AdvancedReportIndexStateModel) {

        const fb = new FilterBuilder();

        fb.setPage(state.page);
        fb.setSort(state.sort);

        fb.setQuery('ownerOrgKey', state.orgKey);

        return fb.get();
    }

    // TODO REMOVE AFTER MASKS/VARIABLES HAVE BEEN IMPLEMENTED
    private getMaskValue(obj: any, mask: string) {
        //? Demo usage:
        // let demo = {
        //     static : "name",
        //     dynamic: "sample.label"
        // }

        // this.getMaskValue(data, "sample.label");

        if (!mask) return "";

        const keys = mask.split(".");

        let result = obj;

        for (let key of keys) {
            if (!result.hasOwnProperty(key)) break;

            result = result[key];
        }

        console.log('result', result)
    }


}